import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddDomainDialog from '../components/AddDomainDialog';
import { useNavigate } from 'react-router-dom';

const AllCertificates = ({ domains, onAddDomain, onDeleteDomain, onRefresh }) => {
  const [openAddDialog, setOpenAddDialog] = React.useState(false);
  const navigate = useNavigate();

  // Function to determine status based on days left
  const getStatus = (daysLeft) => {
    if (daysLeft <= 0) return 'expired';
    if (daysLeft <= 30) return 'expiring';
    return 'active';
  };

  // Process domains to include calculated status
  const processedDomains = domains.map(domain => ({
    ...domain,
    status: getStatus(domain.days_left)
  }));

  const columns = [
    { 
      field: 'domain', 
      headerName: 'Domain', 
      flex: 1,
      headerClassName: 'data-grid-header',
      cellClassName: 'data-grid-cell'
    },
    { 
      field: 'customer', 
      headerName: 'Customer', 
      flex: 1,
      headerClassName: 'data-grid-header',
      cellClassName: 'data-grid-cell'
    },
    { 
      field: 'valid_till', 
      headerName: 'Expiry Date', 
      flex: 1,
      headerClassName: 'data-grid-header',
      cellClassName: 'data-grid-cell',
      valueFormatter: (params) => 
        new Date(params.value).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
    },
    { 
      field: 'days_left', 
      headerName: 'Days Left', 
      flex: 0.5,
      headerClassName: 'data-grid-header',
      cellClassName: 'data-grid-cell',
      valueFormatter: (params) => `${params.value} days`
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.7,
      headerClassName: 'data-grid-header',
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: 
              params.value === 'expired' ? '#fef2f2' :
              params.value === 'expiring' ? '#fff7ed' :
              params.value === 'active' ? '#f0fdf4' : '#f8fafc',
            color: 
              params.value === 'expired' ? '#dc2626' :
              params.value === 'expiring' ? '#ea580c' :
              params.value === 'active' ? '#16a34a' : '#64748b',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontWeight: 500,
            textTransform: 'capitalize',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {params.value}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      headerClassName: 'data-grid-header',
      renderCell: (params) => (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => onDeleteDomain(params.row.domain)}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Delete
        </Button>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, alignItems: 'center' }}>
        <Typography 
          variant="h5" 
          component="h2"
          sx={{ 
            fontSize: '1.5rem',
            color: '#1e293b',
            fontWeight: 600
          }}
        >
          All SSL Certificates
        </Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setOpenAddDialog(true)}
            sx={{ 
              mr: 2,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Add Domain
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/')}
            sx={{ 
              mr: 2,
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            View Expiring Certificates
          </Button>
          <Button 
            variant="outlined" 
            onClick={onRefresh}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: '8px',
              padding: '8px 16px'
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper 
        elevation={0} 
        sx={{ 
          height: 600, 
          width: '100%',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          '& .data-grid-header': {
            backgroundColor: '#f8fafc',
            color: '#475569',
            fontSize: '0.875rem',
            fontWeight: 600
          },
          '& .data-grid-cell': {
            fontSize: '0.875rem',
            color: '#334155'
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid #e2e8f0',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f1f5f9'
          }
        }}
      >
        <DataGrid
          rows={processedDomains.map((domain, index) => ({
            id: index,
            ...domain
          }))}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10]}
          disableSelectionOnClick
          density="comfortable"
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': {
              '&.expiring': {
                backgroundColor: '#fff7ed',
                '&:hover': {
                  backgroundColor: '#ffedd5'
                }
              },
              '&.expired': {
                backgroundColor: '#fef2f2',
                '&:hover': {
                  backgroundColor: '#fee2e2'
                }
              },
              '&:hover': {
                backgroundColor: '#f8fafc'
              }
            }
          }}
          getRowClassName={(params) => {
            if (params.row.status === 'expired') return 'expired';
            if (params.row.status === 'expiring') return 'expiring';
            return '';
          }}
        />
      </Paper>

      <AddDomainDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onAdd={onAddDomain}
      />
    </Box>
  );
};

export default AllCertificates; 